import { Fragment, useState } from "react"
import { Text, Button } from "../../../components"
import { Modal } from "../../get-qr-admin"
import { CrmRequest, PAGE_SIZE, copyToClipboard, ensureOk, formatDateTime, getErrorMessage, usePagedList } from "../api"
import { ConfirmButton, ListStatus, Pager } from "../components"

interface CrmClient {
    id: number;
    mail: string | null;
    login: string;
    stores_count: number;
    support_expires_at: string | null;
}

interface SupportAccess {
    clientId: number;
    login: string;
    password: string;
    expires_at: string;
}

const CLIENT_LOGIN_URL = `${window.location.origin}/admin`

export const ClientsTab = ({request}: {request: CrmRequest}) => {
    const {items, loading, error, offset, setOffset, hasNext, reload} = usePagedList<CrmClient>(request, '/crm/get-clients', 'clients')
    const [busyId, setBusyId] = useState<number | null>(null)
    const [actionError, setActionError] = useState<string | null>(null)
    const [access, setAccess] = useState<SupportAccess | null>(null)
    const [copied, setCopied] = useState<'login' | 'password' | null>(null)

    const createSupportPassword = async (client: CrmClient) => {
        setBusyId(client.id)
        setActionError(null)
        try {
            const res = await request(`/crm/create-support-password?client_id=${client.id}`, {method: 'POST'})
            if (!res) return
            await ensureOk(res, 'Не удалось создать временный пароль')
            const data = await res.json()
            setAccess({clientId: client.id, login: data.login, password: data.password, expires_at: data.expires_at})
            setCopied(null)
            await reload()
        } catch (err) {
            setActionError(getErrorMessage(err, 'Не удалось создать временный пароль'))
        } finally {
            setBusyId(null)
        }
    }

    const revokeSupportPassword = async (clientId: number) => {
        setBusyId(clientId)
        setActionError(null)
        try {
            const res = await request(`/crm/revoke-support-password?client_id=${clientId}`, {method: 'DELETE'})
            if (!res) return
            await ensureOk(res, 'Не удалось отозвать доступ')
            setAccess(null)
            await reload()
        } catch (err) {
            setActionError(getErrorMessage(err, 'Не удалось отозвать доступ'))
        } finally {
            setBusyId(null)
        }
    }

    const deleteClient = async (clientId: number) => {
        setBusyId(clientId)
        setActionError(null)
        try {
            const res = await request(`/crm/delete-client?client_id=${clientId}`, {method: 'DELETE'})
            if (!res) return
            await ensureOk(res, 'Не удалось удалить клиента')
            if (items.length === 1 && offset > 0) setOffset(offset - PAGE_SIZE)
            else await reload()
        } catch (err) {
            setActionError(getErrorMessage(err, 'Не удалось удалить клиента'))
        } finally {
            setBusyId(null)
        }
    }

    const copyAccess = async (field: 'login' | 'password') => {
        if (!access) return
        try {
            await copyToClipboard(access[field])
            setCopied(field)
            setTimeout(() => setCopied((value) => (value === field ? null : value)), 1500)
        } catch {
            setActionError('Не удалось скопировать')
        }
    }

    return (
        <div className="crm-list">
            {actionError && <Text size="xs" color="accent">{actionError}</Text>}

            <ListStatus loading={loading} error={error} empty={items.length === 0} emptyText="Клиентов нет" />

            {!loading && items.map((client) => (
                <div className="link-row admin-link-row" key={client.id}>
                    <div className="admin-link-info">
                        <Text size="m" color="white">{client.login}</Text>
                        <span className="admin-link-metric">
                            {`#${client.id} · ${client.mail || 'почта не указана'} · страниц: ${client.stores_count}`}
                        </span>
                        {client.support_expires_at && (
                            <span className="admin-template-badge">{`Доступ поддержки до ${formatDateTime(client.support_expires_at)}`}</span>
                        )}
                    </div>
                    <div className="crm-row-actions">
                        <button type="button" className="admin-edit-btn" disabled={busyId === client.id} onClick={() => createSupportPassword(client)}>
                            <Text size="xs" color="lightGray">{busyId === client.id ? 'Подождите…' : 'Доступ'}</Text>
                        </button>
                        <ConfirmButton label="Удалить" disabled={busyId === client.id} onConfirm={() => deleteClient(client.id)} />
                    </div>
                </div>
            ))}

            <Pager offset={offset} hasNext={hasNext} loading={loading} onChange={setOffset} />

            {access && (
                <Modal title="Временный доступ" onClose={() => setAccess(null)}>
                    <div className="admin-profile-edit">
                        <Text size="s" color="lightGray">
                            {`Войдите в профиль клиента на ${CLIENT_LOGIN_URL} с этими данными. Пароль действует до ${formatDateTime(access.expires_at)}, собственный пароль клиента не меняется.`}
                        </Text>
                        <div className="crm-access-box">
                            {(['login', 'password'] as const).map((field) => (
                                <Fragment key={field}>
                                    <Text size="s" color="lightGray">{field === 'login' ? 'Логин' : 'Пароль'}</Text>
                                    <Text size="m" color="white">{access[field]}</Text>
                                    <button type="button" className="admin-edit-btn" onClick={() => copyAccess(field)}>
                                        <Text size="xs" color={copied === field ? 'accent' : 'lightGray'}>{copied === field ? 'Скопировано' : 'Скопировать'}</Text>
                                    </button>
                                </Fragment>
                            ))}
                        </div>
                        <Text size="xs" color="lightGray">Пароль показывается один раз — после закрытия окна его можно только перевыпустить.</Text>
                        <div className="admin-edit-actions">
                            <Button type="button" variant="ghost" textSize="s" textColor="lightGray" disabled={busyId === access.clientId} onClick={() => revokeSupportPassword(access.clientId)}>Отозвать</Button>
                            <Button type="button" variant="solid" textSize="s" onClick={() => window.open(CLIENT_LOGIN_URL, '_blank', 'noopener,noreferrer')}>Открыть вход</Button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    )
}
